import { deepEqual } from '@empathybroker/deep-equal';
import { Instance } from '@empathybroker/tsclient-play/dist/models/instance/instance';

import { Location, Route, RouteRecord } from 'vue-router';
import { Store } from 'vuex';

import { CONTAINER, TYPES } from '../config';

import { DomainEnum, FeatureIdEnum, PermissionLevelEnum } from '../enums';

import { AuthTokenInfo, ContextState, FeatureNode, FeaturesTree, GuardClass, GuardResolveFunction } from '../models';

import { ROUTER_CONSTS } from '../router/router.consts';

import { AUTH_CONSTS } from '../store/auth/auth.consts';
import { CONFIG_CONSTS } from '../store/config/config.consts';
import { CONTEXT_CONSTS } from '../store/context/context.consts';

import { FeaturesTreeUtils, FiltersUtils } from '../utils';

export class SetContextWithUrlGuard extends GuardClass {

  public static async execute(to: Route, from: Route, next: GuardResolveFunction): Promise<void> {
    let redirectLocation: Location;
    const store = CONTAINER.get<Store<any>>(TYPES.Store);
    const context: ContextState = store.getters[`${CONTEXT_CONSTS.moduleName}/${CONTEXT_CONSTS.getters.context}`];
    const instances: Instance[] = store.getters[`${CONFIG_CONSTS.moduleName}/${CONFIG_CONSTS.getters.instances}`];
    const instanceConfig = SetContextWithUrlGuard.getInstanceConfig(context, to, instances);
    if (!!instanceConfig) {
      const authTokenInfo: AuthTokenInfo = store.getters[`${ AUTH_CONSTS.moduleName }/${ AUTH_CONSTS.getters.tokenInfo }`];
      const featuresTree = FeaturesTreeUtils.getFeaturesTree(instanceConfig, authTokenInfo.authorization);
      let domain: DomainEnum;
      to.matched.some((route: RouteRecord) => {
        if (!!route.meta) {
          domain = (route.meta.domain as DomainEnum);
        }
        return !!domain;
      });
      if (!!to.meta && SetContextWithUrlGuard.isRouteAccesible(to, featuresTree, domain)) {
        const filters = FiltersUtils.getFiltersFromContextAndRoute(context, to, instanceConfig);
        const query = SetContextWithUrlGuard.getQuery(context, to);
        const newContext: ContextState = {
          instanceId: instanceConfig.instanceId,
          environment: context.environment,
          domain: domain,
          filters: filters,
          query: query
        };
        await store.dispatch(`${CONTEXT_CONSTS.moduleName}/${CONTEXT_CONSTS.actions.setContext}`, newContext);
        const contextRoute = SetContextWithUrlGuard.updateRouteWithContext(to, newContext);
        if (!deepEqual(contextRoute, to)) {
          redirectLocation = contextRoute;
        }
      } else {
        redirectLocation = SetContextWithUrlGuard.getDefaultRoute(to, featuresTree);
      }
    }
    if (!!redirectLocation) {
      redirectLocation.replace = true;
    }
    next(redirectLocation);
  }

  public static updateRouteWithContext(route: Route, context: ContextState): Route {
    let updatedRoute: Route = {
      ...route,
      params: {
        ...route.params,
        [ROUTER_CONSTS.params.instanceId]: context.instanceId
      },
      query: {
        ...route.query
      }
    };

    updatedRoute = FiltersUtils.updateRouteFiltersWithContext(updatedRoute, context);

    if (!!context.query) {
      updatedRoute.query[ROUTER_CONSTS.query.query] = context.query;
    } else {
      delete updatedRoute.query[ROUTER_CONSTS.query.query];
    }

    return updatedRoute;
  }

  public static getFeatureNode(featureId: (FeatureIdEnum | string), featureNodes: FeatureNode[]): FeatureNode {
    for (const featureNode of featureNodes) {
      if (featureNode.id === featureId) {
        return featureNode;
      } else if (!!featureNode.children) {
        const childFeatureNode = SetContextWithUrlGuard.getFeatureNode(featureId, featureNode.children);
        if (!!childFeatureNode) {
          return childFeatureNode;
        }
      }
    }
  }

  private static getInstanceConfig(context: ContextState, route: Route, instances: Instance[]): Instance {
    let instanceConfig: Instance;
    const instanceId = (context.instanceId || route.params[ROUTER_CONSTS.params.instanceId]);
    if (!!instanceId) {
      instanceConfig = instances.find((instance: Instance) => (instance.instanceId === instanceId));
    }
    if (!instanceConfig && (instances.length === 1)) {
      instanceConfig = instances[0];
    }
    return instanceConfig;
  }

  private static isRouteAccesible(route: Route, featuresTree: FeaturesTree, domain?: DomainEnum): boolean {
    const domainFeatureNodes = (!!domain ? (featuresTree[domain] || []) : featuresTree.common);
    const featureNode = SetContextWithUrlGuard.getFeatureNode(route.meta.featureId, domainFeatureNodes);
    return (!!featureNode &&
      ((featureNode.permissionLevel !== PermissionLevelEnum.NONE) && (featureNode.permissionLevel !== PermissionLevelEnum.PREVIEW)));
  }

  private static getQuery(context: ContextState, route: Route): string {
    return (context.query || (route.query[ROUTER_CONSTS.query.query] as string));
  }

  private static getDefaultRoute(route: Route, featuresTree: FeaturesTree): Route {
    let defaultRouteName: string;
    Object.keys(featuresTree).some((domainStr: string) => {
      defaultRouteName = SetContextWithUrlGuard.getDefaultRouteName(featuresTree[domainStr]);
      return !!defaultRouteName;
    });
    return {
      ...route,
      name: (defaultRouteName || ROUTER_CONSTS.routes.pageNotFound)
    };
  }

  private static getDefaultRouteName(featureNodes: FeatureNode[]): string {
    for (const featureNode of featureNodes) {
      if ((featureNode.permissionLevel !== PermissionLevelEnum.NONE) && (featureNode.permissionLevel !== PermissionLevelEnum.PREVIEW)) {
        if (!!featureNode.routeName) {
          return featureNode.routeName;
        } else if (!!featureNode.children) {
          const defaultRouteName = SetContextWithUrlGuard.getDefaultRouteName(featureNode.children);
          if (!!defaultRouteName) {
            return defaultRouteName;
          }
        }
      }
    }
  }

}
